import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert, Table, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { previewDriverPayments, confirmDriverPayments } from '../api';

const DriverPayments = () => {
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  
  const [preliqFile, setPreliqFile] = useState(null);
  const [flotaFile, setFlotaFile] = useState(null);
  const [contadorFile, setContadorFile] = useState(null);
  
  const [previewData, setPreviewData] = useState(null);

  const handlePreview = async () => {
    if (!preliqFile || !flotaFile || !contadorFile) {
      toast.warning("Faltan archivos requeridos.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await previewDriverPayments(preliqFile, flotaFile, contadorFile);
      setPreviewData(result);
      toast.success(`Se encontraron ${result.length} liquidaciones listas para procesar.`);
    } catch (err) {
      console.error(err);
      setError("Error al procesar los archivos. Revisa que el formato sea correcto.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!previewData || previewData.length === 0) return;
    setConfirming(true);
    setError(null);
    try {
      const response = await confirmDriverPayments(previewData);
      
      toast.success(response.message);

      if (response.file_base64) {
        const link = document.createElement('a');
        link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.file_base64}`;
        link.download = response.file_name || 'Planilla_Banco.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setPreviewData(null);
      setPreliqFile(null);
      setFlotaFile(null);
      setContadorFile(null);

    } catch (err) {
      console.error(err);
      setError("Error crítico al intentar crear las transacciones en la base de datos.");
    } finally {
      setConfirming(false);
    }
  };

  const formatMoney = (val) => "$" + Math.round(val || 0).toLocaleString('es-CL');

  return (
    <Container fluid className="p-4 md:p-8 bg-slate-50 min-h-screen text-slate-800" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="max-w-[1200px] mx-auto">
        
        <header className="mb-8 flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="outline-secondary" size="sm" className="fw-bold px-3 rounded-pill border-slate-200">
                ← Volver al Dashboard
              </Button>
            </Link>
            <div>
              <p className="text-[9px] font-black uppercase text-success tracking-[0.4em] mb-0.5 text-green-600">Automatización</p>
              <h1 className="text-xl font-black text-slate-700 tracking-tight uppercase">Pagos a Conductores</h1>
            </div>
          </div>
          <div className="text-right">
             <span className="badge bg-green-100 text-green-700 font-bold px-3 py-2 rounded-pill uppercase tracking-widest text-[10px]">Módulo Beta</span>
          </div>
        </header>

        {error && <Alert variant="danger" className="rounded-2xl border-0 shadow-sm mb-6 font-bold">{error}</Alert>}

        {!previewData ? (
          <Row>
            <Col md={12}>
              <Card className="border-0 shadow-sm rounded-3xl overflow-hidden bg-white">
                <Card.Body className="p-8">
                  <h4 className="font-black text-slate-700 mb-8 italic border-b pb-4 border-slate-50 text-uppercase tracking-tighter">1. Carga de Documentos</h4>
                  
                  <Form className="space-y-6">
                    <div className="bg-slate-50/50 p-6 rounded-2xl border border-dashed border-slate-200 hover:border-green-400 transition-colors mb-4">
                      <Form.Group>
                        <Form.Label className="font-black text-[10px] uppercase tracking-widest text-slate-500 mb-2 block">A. Preliquidaciones sin formatear (.XLS)</Form.Label>
                        <Form.Control type="file" accept=".xls" onChange={(e) => setPreliqFile(e.target.files[0])} className="form-control-lg bg-white border-slate-200 rounded-xl text-sm" />
                      </Form.Group>
                    </div>

                    <div className="bg-slate-50/50 p-6 rounded-2xl border border-dashed border-slate-200 hover:border-blue-400 transition-colors mb-4">
                      <Form.Group>
                        <Form.Label className="font-black text-[10px] uppercase tracking-widest text-slate-500 mb-2 block">B. Detalle de la Flota (.XLSX)</Form.Label>
                        <Form.Control type="file" accept=".xlsx" onChange={(e) => setFlotaFile(e.target.files[0])} className="form-control-lg bg-white border-slate-200 rounded-xl text-sm" />
                      </Form.Group>
                    </div>

                    <div className="bg-slate-50/50 p-6 rounded-2xl border border-dashed border-slate-200 hover:border-purple-400 transition-colors mb-6">
                      <Form.Group>
                        <Form.Label className="font-black text-[10px] uppercase tracking-widest text-slate-500 mb-2 block">C. Reporte de Contador Liquidado (.XLSX)</Form.Label>
                        <Form.Control type="file" accept=".xlsx" onChange={(e) => setContadorFile(e.target.files[0])} className="form-control-lg bg-white border-slate-200 rounded-xl text-sm" />
                      </Form.Group>
                    </div>

                    <Button 
                      variant="primary" 
                      onClick={handlePreview} 
                      disabled={loading || !preliqFile || !flotaFile || !contadorFile}
                      className="w-100 py-4 rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-indigo-100 hover:shadow-indigo-200 transition-all border-b-4 border-indigo-700 bg-indigo-600 hover:bg-indigo-500 active:border-0 active:translate-y-1"
                    >
                      {loading ? <Spinner animation="border" size="sm" className="me-2" /> : "Generar Previsualización"}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : (
          <Row>
            <Col md={12}>
              <Card className="border-0 shadow-sm rounded-3xl overflow-hidden bg-white mb-6">
                <Card.Header className="bg-slate-900 text-white p-6 d-flex justify-content-between align-items-center border-0">
                  <div>
                    <h4 className="font-black italic text-uppercase tracking-tighter m-0">2. Previsualización de Pagos</h4>
                    <p className="text-slate-400 text-xs font-bold m-0 uppercase tracking-widest mt-1">Verifica los datos antes de confirmar</p>
                  </div>
                  <Badge bg="success" className="fs-6 py-2 px-3 rounded-pill border border-green-400">{previewData.length} Registros</Badge>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="max-h-[500px] overflow-y-auto">
                    <Table hover className="mb-0 text-sm align-middle">
                      <thead className="bg-slate-50 sticky-top shadow-sm">
                        <tr>
                          <th className="px-6 py-3 font-black text-[10px] uppercase tracking-widest text-slate-400 border-0">Nº Preliq</th>
                          <th className="px-6 py-3 font-black text-[10px] uppercase tracking-widest text-slate-400 border-0">Conductor</th>
                          <th className="px-6 py-3 font-black text-[10px] uppercase tracking-widest text-slate-400 border-0 text-center">Móvil</th>
                          <th className="px-6 py-3 font-black text-[10px] uppercase tracking-widest text-slate-400 border-0">Período</th>
                          <th className="px-6 py-3 font-black text-[10px] uppercase tracking-widest text-slate-400 border-0 text-end">A Pagar</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {previewData.map((row, idx) => (
                          <tr key={idx} className="hover:bg-slate-50/50">
                            <td className="px-6 py-3 font-bold text-slate-600">{row.preliq_num}</td>
                            <td className="px-6 py-3">
                                <div className="d-flex align-items-center gap-2">
                                  <span className="font-bold text-slate-800">{row.conductor_flota}</span>
                                  {row.missing_info && (
                                    <Badge bg="warning" text="dark" className="text-[8px] uppercase px-2 py-1 rounded-pill">
                                      Sin Cuenta
                                    </Badge>
                                  )}
                                </div>
                            </td>
                            <td className="px-6 py-3 text-center">
                              <Badge bg="light" text="dark" className="border text-slate-500 font-black">{row.movil}</Badge>
                            </td>
                            <td className="px-6 py-3 text-xs font-medium text-slate-500">{row.periodo}</td>
                            <td className="px-6 py-3 font-black text-success text-end">{formatMoney(row.monto_cancelar)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
                <Card.Footer className="bg-white p-6 border-t border-slate-100 d-flex gap-4">
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => setPreviewData(null)}
                    className="w-25 py-3 rounded-xl font-bold uppercase tracking-wider"
                    disabled={confirming}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    variant="success" 
                    onClick={handleConfirm}
                    disabled={confirming}
                    className="w-75 py-3 rounded-xl font-black uppercase tracking-widest shadow-lg shadow-green-100 border-b-4 border-green-700 hover:bg-green-600 active:border-0 active:translate-y-1"
                  >
                    {confirming ? <Spinner animation="border" size="sm" className="me-2" /> : "Confirmar Pagos y Descargar Planilla Banco"}
                  </Button>
                </Card.Footer>
              </Card>
            </Col>
          </Row>
        )}

        <footer className="mt-8 text-center pb-8">
          <p className="text-slate-300 text-[10px] uppercase font-black tracking-[0.5em]">Transportes Emilia | Sistema de Pagos Automatizado</p>
        </footer>
      </div>
    </Container>
  );
};

export default DriverPayments;